import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, KeyRound, Laptop, LogOut, Pencil, Plus, Smartphone, Trash2, Tv, X } from "lucide-react";

import { ConnectedApps } from "../components/ConnectedApps.jsx";
import { Notifications } from "../components/Notifications.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { Modal, Spinner, Tabs, ago, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useUrlStates } from "../lib/useUrlState.js";

const SCOPES = ["projects:read", "projects:write", "deploy", "env:read", "env:write", "infra:read", "infra:write", "sessions:run", "projects:admin", "tokens:admin", "live"];

/** The recommended set for an agent: it can set a secret and deploy, never read one back. */
const AGENT_SCOPES = ["projects:read", "projects:write", "deploy", "env:write"];

const TABS = [
    { id: "notifications", label: "Notifications" },
    { id: "live", label: "Live" },
    // The id stays `passkeys` so links into this tab keep working.
    { id: "passkeys", label: "Security" },
    { id: "tokens", label: "API tokens" },
    { id: "apps", label: "Connected apps" }
];

export function Settings() {
    // The tab is part of the address, as it is on a project: a link to the
    // tokens should open on the tokens.
    const [{ tab }, setUrl] = useUrlStates({ tab: "notifications" });

    return (
        <>
            <PageHeader title="Settings">
                <div className="mt-3">
                    <Tabs tabs={TABS} active={tab} onChange={(value) => setUrl("tab", value)} />
                </div>
            </PageHeader>
            {tab === "notifications" ? <Notifications /> : null}
            {tab === "live" ? <LiveSettings /> : null}
            {tab === "passkeys" ? <Security /> : null}
            {tab === "tokens" ? <Tokens /> : null}
            {tab === "apps" ? <ConnectedApps /> : null}
        </>
    );
}

// --- the wall -------------------------------------------------------------------

const LAYOUTS = [
    { id: 1, name: "Bridge", line: "The operations wall: figures across the top, the fleet as tiles, what is happening now down the right." },
    { id: 2, name: "Stage", line: "One thing at a time, large: a deployment while one runs, the incidents while any are open, the box breathing otherwise." },
    { id: 3, name: "Stream", line: "Time on the left, state on the right: events flow down, every watched component is a square." }
];

/** A thumbnail of a layout, drawn in blocks. */
function Schematic({ id }) {
    const block = "rounded-[3px] bg-gray-300 dark:bg-gray-600";
    const soft = "rounded-[3px] bg-gray-200 dark:bg-gray-700";
    if (id === 1) {
        return (
            <div className="grid aspect-video grid-rows-[3px_6px_1fr_7px] gap-1 rounded-md bg-gray-100 p-1.5 dark:bg-gray-800" aria-hidden>
                <div className={`${block} w-1/3`} />
                <div className="grid grid-cols-6 gap-1">{Array.from({ length: 6 }, (_, i) => <div key={i} className={soft} />)}</div>
                <div className="grid grid-cols-3 gap-1">
                    <div className="col-span-2 grid grid-cols-4 gap-1">{Array.from({ length: 8 }, (_, i) => <div key={i} className={i === 1 ? "rounded-[3px] bg-red-400" : soft} />)}</div>
                    <div className="grid grid-rows-2 gap-1"><div className="rounded-[3px] bg-sky-400" /><div className={soft} /></div>
                </div>
                <div className="grid grid-cols-[1fr_1fr_1fr_2fr] gap-1">{Array.from({ length: 4 }, (_, i) => <div key={i} className={soft} />)}</div>
            </div>
        );
    }
    if (id === 2) {
        return (
            <div className="grid aspect-video grid-rows-[3px_1fr_5px] gap-1 rounded-md bg-gray-100 p-1.5 dark:bg-gray-800" aria-hidden>
                <div className={`${block} w-1/3`} />
                <div className="grid grid-cols-[1fr_2.6fr_1.1fr] gap-1">
                    <div className="grid grid-rows-6 gap-0.5">{Array.from({ length: 6 }, (_, i) => <div key={i} className={soft} />)}</div>
                    <div className="flex items-center justify-center rounded-[3px] bg-sky-400/25"><div className="size-1/3 rounded-full bg-emerald-400" /></div>
                    <div className="grid grid-rows-5 gap-0.5">{Array.from({ length: 5 }, (_, i) => <div key={i} className={soft} />)}</div>
                </div>
                <div className={soft} />
            </div>
        );
    }
    return (
        <div className="grid aspect-video grid-rows-[3px_1fr] gap-1 rounded-md bg-gray-100 p-1.5 dark:bg-gray-800" aria-hidden>
            <div className={`${block} w-1/3`} />
            <div className="grid grid-cols-[1fr_1.15fr] gap-1">
                <div className="grid grid-rows-[2fr_1fr_1fr_1fr_1fr] gap-0.5"><div className="rounded-[3px] bg-sky-400" />{Array.from({ length: 4 }, (_, i) => <div key={i} className={soft} />)}</div>
                <div className="grid grid-rows-[1fr_6px] gap-1">
                    <div className="grid grid-cols-6 content-start gap-0.5 rounded-[3px] bg-gray-200 p-1 dark:bg-gray-700">{Array.from({ length: 18 }, (_, i) => <div key={i} className={`aspect-square rounded-[2px] ${i === 7 ? "bg-red-400" : "bg-emerald-400/70"}`} />)}</div>
                    <div className="grid grid-cols-3 gap-1">{Array.from({ length: 3 }, (_, i) => <div key={i} className={soft} />)}</div>
                </div>
            </div>
        </div>
    );
}

/**
 * The wall: which layout it shows, and which screens may show it.
 *
 * A screen is paired with a token that holds the `live` scope and nothing
 * else, handed over once as the fragment of a URL — the one part of an address
 * a server never sees. The list below is the tokens list filtered to those,
 * and revoking one is what unpairs the screen.
 */
function LiveSettings() {
    const [preferences, setPreferences] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [pairing, setPairing] = useState(false);
    const [name, setName] = useState("Office TV");
    const [paired, setPaired] = useState(null);
    const toast = useToast();

    const loadTokens = () => api.tokens.list().then(setTokens).catch(() => setTokens([]));
    useEffect(() => {
        api.preferences.get().then(setPreferences).catch(() => setPreferences({ live: { layout: 1 } }));
        loadTokens();
    }, []);

    const current = preferences?.live?.layout ?? null;

    const choose = async (layout) => {
        const before = preferences;
        setPreferences({ ...preferences, live: { ...preferences?.live, layout } });
        try {
            await api.preferences.update({ live: { layout } });
            toast(`The wall shows ${LAYOUTS.find((item) => item.id === layout).name}`);
        } catch (error) {
            setPreferences(before);
            toast(error.message, "error");
        }
    };

    const pair = async () => {
        try {
            const result = await api.tokens.create({ name: name.trim(), scopes: ["live"] });
            setPaired({ ...result, name: name.trim() });
            setPairing(false);
            loadTokens();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const unpair = async (token) => {
        try {
            await api.tokens.revoke(token.id);
            toast(`Unpaired ${token.name}`);
            loadTokens();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const screens = (tokens ?? []).filter((token) => token.scopes.length === 1 && token.scopes[0] === "live" && !token.revokedAt);
    const link = paired ? `${window.location.origin}/live#key=${paired.token}` : "";

    return (
        <div className="space-y-6">
            <section className="space-y-2">
                <div className="flex items-center justify-between">
                    <h2 className="section-title">Layout</h2>
                    <a className="btn-ghost" href="/live" target="_blank" rel="noreferrer"><ExternalLink size={14} /> Open the wall</a>
                </div>
                {!preferences ? (
                    <Spinner className="muted" />
                ) : (
                    <div className="grid gap-3 sm:grid-cols-3">
                        {LAYOUTS.map((layout) => (
                            <div
                                key={layout.id}
                                className={`card flex min-w-0 flex-col p-3 transition-colors ${current === layout.id ? "border-blue-500 ring-1 ring-blue-500" : ""}`}
                            >
                                <button className="min-w-0 text-left" onClick={() => choose(layout.id)} aria-pressed={current === layout.id} aria-label={`Show ${layout.name} on the wall`}>
                                    <Schematic id={layout.id} />
                                    <div className="mt-3 flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium">{layout.name}</span>
                                        {current === layout.id ? <span className="badge-running">on the wall</span> : null}
                                    </div>
                                    <p className="muted mt-1 text-xs">{layout.line}</p>
                                </button>
                                <a href={`/live/${layout.id}`} target="_blank" rel="noreferrer" className="link mt-2 inline-flex items-center gap-1 text-xs">
                                    <ExternalLink size={12} /> Preview
                                </a>
                            </div>
                        ))}
                    </div>
                )}
                <p className="text-xs muted">
                    <code className="font-mono">/live</code> shows the chosen layout and follows a change here within a few seconds, without being touched.
                    <code className="font-mono"> /live/1</code>, <code className="font-mono">/live/2</code> and <code className="font-mono">/live/3</code> always show that one.
                </p>
            </section>

            <section className="space-y-2">
                <div className="flex items-center justify-between">
                    <h2 className="section-title">Screens</h2>
                    <button className="btn-primary" onClick={() => setPairing(true)}><Plus size={14} /> Pair a screen</button>
                </div>
                <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                    {!tokens ? (
                        <div className="px-3 py-4"><Spinner className="muted" /></div>
                    ) : screens.length === 0 ? (
                        <p className="px-3 py-4 text-sm muted">No screen is paired. A signed-in browser can still open the wall; a TV needs a key.</p>
                    ) : (
                        screens.map((token) => (
                            <div key={token.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
                                <div className="flex min-w-0 items-center gap-3">
                                    <Tv size={16} className="muted shrink-0" />
                                    <div className="min-w-0">
                                        <p className="flex flex-wrap items-center gap-2">
                                            {token.name}
                                            <code className="muted font-mono text-xs">ops_{token.prefix}_…</code>
                                        </p>
                                        <p className="muted truncate text-xs">{token.lastUsedAt ? `last seen ${ago(token.lastUsedAt)}` : "has not opened the wall yet"}</p>
                                    </div>
                                </div>
                                <button className="btn-ghost px-2 py-1" onClick={() => unpair(token)} aria-label={`Unpair ${token.name}`}>Unpair</button>
                            </div>
                        ))
                    )}
                </div>
                <p className="text-xs muted">
                    A paired screen holds a key that reads the wall and nothing else — no manifest, no env, no log. Unpairing revokes it;
                    the record stays under API tokens.
                </p>
            </section>

            <Modal open={pairing} onClose={() => setPairing(false)} title="Pair a screen">
                <div className="space-y-3">
                    <div>
                        <label className="label mb-1" htmlFor="screen-name">Name</label>
                        <input id="screen-name" className="input" value={name} onChange={(event) => setName(event.target.value)} placeholder="Office TV" />
                    </div>
                    <p className="text-xs muted">You will get a link to open once on that screen. It carries the key, and the screen keeps it.</p>
                    <button className="btn-primary w-full" onClick={pair} disabled={!name.trim()}>Pair</button>
                </div>
            </Modal>

            <Modal open={Boolean(paired)} onClose={() => setPaired(null)} title="Screen paired">
                {paired ? (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-600 dark:muted">
                            Open this link once on <strong>{paired.name}</strong>. It is the only time it is shown.
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 break-all rounded-md bg-gray-100 p-2 font-mono text-xs dark:bg-gray-800">{link}</code>
                            <button
                                className="btn-ghost"
                                onClick={() => {
                                    navigator.clipboard.writeText(link);
                                    toast("Copied");
                                }}
                                aria-label="Copy the link"
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                        <p className="text-xs muted">
                            The key travels after the <code className="font-mono">#</code>, which never reaches a server or a log. The screen stores it
                            and takes it out of the address; from then on it just shows <code className="font-mono">/live</code>.
                        </p>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
}

/** What can sign in, and what stays signed in. */
function Security() {
    return (
        <div className="space-y-6">
            <Passkeys />
            <Devices />
        </div>
    );
}

function Passkeys() {
    const [passkeys, setPasskeys] = useState(null);
    const toast = useToast();

    const load = () => api.auth.passkeys().then(setPasskeys).catch(() => setPasskeys([]));
    // Braces matter: an effect may return only a cleanup function, and `load`
    // returns a promise. Passing it bare makes React call that promise on unmount.
    useEffect(() => {
        load();
    }, []);

    if (!passkeys) return <Spinner className="muted" />;

    return (
        <section className="space-y-2">
            <h2 className="section-title">Passkeys</h2>
            <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                {passkeys.map((passkey) => (
                    <PasskeyRow
                        key={passkey.id}
                        passkey={passkey}
                        onlyOne={passkeys.length <= 1}
                        onChanged={load}
                    />
                ))}
            </div>
            <p className="text-xs muted">
                To add a passkey on another device, open this page there and sign in — enrolment from a signed-in session
                does not need the bootstrap code.
            </p>
        </section>
    );
}

function PasskeyRow({ passkey, onlyOne, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [label, setLabel] = useState(passkey.label);
    const toast = useToast();

    const save = async () => {
        const next = label.trim();
        if (!next || next === passkey.label) return setEditing(false);
        try {
            await api.auth.renamePasskey(passkey.id, next);
            toast(`Renamed to ${next}`);
            setEditing(false);
            onChanged();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    return (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
            {editing ? (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <KeyRound size={14} className="muted shrink-0" />
                    <input
                        className="input"
                        value={label}
                        autoFocus
                        onChange={(event) => setLabel(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") save();
                            if (event.key === "Escape") {
                                setLabel(passkey.label);
                                setEditing(false);
                            }
                        }}
                        aria-label="Passkey name"
                    />
                    <button className="btn-ghost px-2 py-1" onClick={save} aria-label="Save name"><Check size={13} /></button>
                    <button
                        className="btn-ghost px-2 py-1"
                        onClick={() => {
                            setLabel(passkey.label);
                            setEditing(false);
                        }}
                        aria-label="Cancel"
                    >
                        <X size={13} />
                    </button>
                </div>
            ) : (
                <>
                    <div className="flex min-w-0 items-center gap-2">
                        <KeyRound size={14} className="muted shrink-0" />
                        <span className="truncate">{passkey.label}</span>
                        <span className="muted shrink-0 text-xs">
                            {passkey.deviceType === "multiDevice" ? "synced" : "device-bound"}
                            {passkey.lastUsedAt ? ` · used ${ago(passkey.lastUsedAt)}` : " · never used"}
                        </span>
                    </div>
                    <div className="flex shrink-0 gap-1">
                        <button className="btn-ghost px-2 py-1" onClick={() => setEditing(true)} aria-label={`Rename ${passkey.label}`}>
                            <Pencil size={12} />
                        </button>
                        <button
                            className="btn-danger px-2 py-1"
                            disabled={onlyOne}
                            aria-label={`Remove ${passkey.label}`}
                            title={onlyOne ? "Enrol another before removing this one" : "Remove"}
                            onClick={async () => {
                                try {
                                    await api.auth.removePasskey(passkey.id);
                                    toast(`Removed ${passkey.label}`);
                                    onChanged();
                                } catch (error) {
                                    toast(error.message, "error");
                                }
                            }}
                        >
                            <Trash2 size={12} />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * Browsers that stay signed in.
 *
 * A row is one browser, not one sign-in: signing in again on a device already
 * here rotates its secret rather than adding a second row nobody could tell
 * apart. Trust lasts until it is taken away here, which is why this list has to
 * be somewhere obvious.
 */
function Devices() {
    const [devices, setDevices] = useState(null);
    const toast = useToast();

    const load = () => api.auth.devices().then(setDevices).catch(() => setDevices([]));
    useEffect(() => {
        load();
    }, []);

    if (!devices) return <Spinner className="muted" />;

    return (
        <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <h2 className="section-title">Trusted devices</h2>
                {devices.length ? (
                    <button
                        className="btn-ghost"
                        onClick={async () => {
                            if (!window.confirm("Sign out everywhere? Every device will need its passkey again, including this one.")) return;
                            try {
                                await api.auth.revokeAllDevices();
                                // Every session is gone, this one included.
                                window.location.reload();
                            } catch (error) {
                                toast(error.message, "error");
                            }
                        }}
                    >
                        <LogOut size={14} /> Sign out everywhere
                    </button>
                ) : null}
            </div>

            {devices.length ? (
                <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                    {devices.map((device) => (
                        <DeviceRow key={device.id} device={device} onChanged={load} />
                    ))}
                </div>
            ) : (
                <p className="card p-4 text-sm muted">
                    No device stays signed in. Tick “Stay signed in on this device” when you sign in to add one.
                </p>
            )}

            <p className="text-xs muted">
                A trusted device skips the passkey until you revoke it here. Revealing environment values, creating an
                API token and deleting a project ask for it again regardless.
            </p>
        </section>
    );
}

function DeviceRow({ device, onChanged }) {
    const toast = useToast();
    const handheld = /iPhone|iPad|Android/.test(device.label);
    const Icon = handheld ? Smartphone : Laptop;

    return (
        <div className="flex items-center justify-between gap-2 p-3 text-sm">
            <div className="flex min-w-0 items-center gap-2">
                <Icon size={14} className="muted shrink-0" />
                <span className="truncate">{device.label}</span>
                {device.current ? <span className="badge-success shrink-0">this browser</span> : null}
                <span className="muted shrink-0 text-xs">
                    {device.lastUsedAt ? `used ${ago(device.lastUsedAt)}` : `added ${ago(device.createdAt)}`}
                </span>
            </div>
            <button
                className="btn-danger shrink-0 px-2 py-1"
                aria-label={`Revoke ${device.label}`}
                title="Revoke"
                onClick={async () => {
                    try {
                        await api.auth.revokeDevice(device.id);
                        if (device.current) return window.location.reload();
                        toast(`${device.label} will need its passkey again`);
                        onChanged();
                    } catch (error) {
                        toast(error.message, "error");
                    }
                }}
            >
                <Trash2 size={12} />
            </button>
        </div>
    );
}

function Tokens() {
    const [tokens, setTokens] = useState(null);
    const [creating, setCreating] = useState(false);
    const [issued, setIssued] = useState(null);
    const [name, setName] = useState("");
    const [scopes, setScopes] = useState(AGENT_SCOPES);
    const toast = useToast();

    const load = () => api.tokens.list().then(setTokens).catch(() => setTokens([]));
    useEffect(() => {
        load();
    }, []);

    const onRevoke = async (token) => {
        try {
            await api.tokens.revoke(token.id);
            toast(`Revoked ${token.name}`);
            load();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const onDelete = async (token) => {
        if (!window.confirm(`Delete the token "${token.name}"? It is already revoked, so nothing stops working — this only removes the record.`)) return;
        try {
            await api.tokens.remove(token.id);
            toast(`Deleted ${token.name}`);
            load();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    if (!tokens) return null;

    const revoked = tokens.filter((token) => token.revokedAt).length;

    const create = async () => {
        try {
            const result = await api.tokens.create({ name: name.trim(), scopes });
            setIssued(result);
            setCreating(false);
            setName("");
            load();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    return (
        <section className="space-y-2">
            <div className="flex items-center justify-between">
                <h2 className="section-title">API tokens</h2>
                <div className="flex gap-2">
                    {revoked > 1 ? (
                        <button
                            className="btn-ghost"
                            onClick={async () => {
                                if (!window.confirm(`Delete all ${revoked} revoked tokens? They already do not work; this only removes the records.`)) return;
                                for (const token of tokens.filter((t) => t.revokedAt)) {
                                    await api.tokens.remove(token.id).catch(() => {});
                                }
                                toast(`Deleted ${revoked} revoked tokens`);
                                load();
                            }}
                        >
                            Clear {revoked} revoked
                        </button>
                    ) : null}
                    <button className="btn-primary" onClick={() => setCreating(true)}><Plus size={14} /> New token</button>
                </div>
            </div>

            <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                {tokens.length === 0 ? (
                    <p className="px-3 py-4 text-sm muted">No tokens. The Claude Code skill needs one.</p>
                ) : (
                    tokens.map((token) => (
                        <div key={token.id} className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5 text-sm ${token.revokedAt ? "opacity-60" : ""}`}>
                            <div className="min-w-0">
                                <p className="flex flex-wrap items-center gap-2">
                                    {token.name}
                                    <code className="muted font-mono text-xs">ops_{token.prefix}_…</code>
                                    {token.revokedAt ? <span className="badge-queued">revoked</span> : null}
                                </p>
                                <p className="muted truncate text-xs">
                                    {token.scopes.join(", ") || "no scopes"} ·{" "}
                                    {token.lastUsedAt ? `last used ${ago(token.lastUsedAt)}` : "never used"}
                                </p>
                            </div>

                            <div className="flex shrink-0 gap-1">
                                {token.revokedAt ? (
                                    // Only once revoked: the server refuses otherwise, so a
                                    // token cannot vanish without that being on the record.
                                    <button
                                        className="btn-danger px-2 py-1"
                                        aria-label={`Delete ${token.name}`}
                                        title="Delete permanently"
                                        onClick={() => onDelete(token)}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                ) : (
                                    <button className="btn-ghost px-2 py-1" onClick={() => onRevoke(token)} aria-label={`Revoke ${token.name}`}>
                                        Revoke
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Modal open={creating} onClose={() => setCreating(false)} title="New API token">
                <div className="space-y-3">
                    <div>
                        <label className="label mb-1" htmlFor="token-name">Name</label>
                        <input id="token-name" className="input" value={name} onChange={(event) => setName(event.target.value)} placeholder="claude" />
                    </div>
                    <div>
                        <p className="label mb-1">Scopes</p>
                        <div className="space-y-1">
                            {SCOPES.map((scope) => (
                                <label key={scope} className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={scopes.includes(scope)}
                                        onChange={(event) =>
                                            setScopes((previous) => (event.target.checked ? [...previous, scope] : previous.filter((s) => s !== scope)))
                                        }
                                    />
                                    <code className="font-mono text-xs">{scope}</code>
                                    {scope === "env:read" ? (
                                        <span className="text-xs text-amber-600 dark:text-amber-400">lets the holder read secrets back</span>
                                    ) : null}
                                </label>
                            ))}
                        </div>
                        <p className="mt-2 text-xs muted">
                            The default set is what an agent needs: it can create a project, set a secret and ship a deploy,
                            but <code className="font-mono">env:read</code> is left off so it can never read a secret back.
                        </p>
                    </div>
                    <button className="btn-primary w-full" onClick={create} disabled={!name.trim() || scopes.length === 0}>Create</button>
                </div>
            </Modal>

            <Modal open={Boolean(issued)} onClose={() => setIssued(null)} title="Token created">
                {issued ? (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-600 dark:muted">
                            This is the only time it is shown. Store it now.
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 break-all rounded-md bg-gray-100 p-2 font-mono text-xs dark:bg-gray-800">{issued.token}</code>
                            <button
                                className="btn-ghost"
                                onClick={() => {
                                    navigator.clipboard.writeText(issued.token);
                                    toast("Copied");
                                }}
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                        <p className="text-xs muted">
                            The skill reads it from <code className="font-mono">OPS_TOKEN</code> or{" "}
                            <code className="font-mono">~/.config/scaling-ops/token</code>.
                        </p>
                    </div>
                ) : null}
            </Modal>
        </section>
    );
}
